// src/components/TeacherAddQuestion.js
import { useState } from 'react';
import { uploadAllPhysicalFiles } from '../../lib/s3Upload';

function TeacherAddQuestion({ sectionId, examId, onQuestionAdded }) {
    const [type, setType] = useState('single');
    const [content, setContent] = useState('');
    const [points, setPoints] = useState(1);
    const [negativePoints, setNegativePoints] = useState(0);
    const [noAnswerPoints, setNoAnswerPoints] = useState(0);
    const [tags, setTags] = useState('');
    const [options, setOptions] = useState(['', '']);
    const [optionFiles, setOptionFiles] = useState([null, null]);
    const [correctIndexes, setCorrectIndexes] = useState([0]);
    const [questionFiles, setQuestionFiles] = useState([]);
    const [message, setMessage] = useState('');

    const handleOptionChange = (index, value) => {
        const updated = [...options];
        updated[index] = value;
        setOptions(updated);
    };

    const handleOptionFileChange = (index, file) => {
        const updated = [...optionFiles];
        updated[index] = file;
        setOptionFiles(updated);
    };

    const handleAddOption = () => {
        setOptions([...options, '']);
        setOptionFiles([...optionFiles, null]);
    };

    const handleRemoveOption = (index) => {
        setOptions(prev => prev.filter((_, i) => i !== index));
        setOptionFiles(prev => prev.filter((_, i) => i !== index));
        setCorrectIndexes(prev => {
            const updated = prev
                .filter(i => i !== index)
                .map(i => (i > index ? i - 1 : i));

            if (type === 'single' && updated.length === 0 && options.length > 1) {
                return [index === 0 ? 0 : index - 1];
            }

            return updated;
        });
    };

    const handleToggleCorrect = (index) => {
        if (type === 'single') {
            setCorrectIndexes([index]);
        } else {
            setCorrectIndexes(prev =>
                prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
            );
        }
    };

    const handleQuestionFilesChange = (e) => {
        setQuestionFiles(prev => [...prev, ...Array.from(e.target.files)]);
    };

    const removeQuestionFile = (index) => {
        setQuestionFiles(prev => prev.filter((_, i) => i !== index));
    };

    const renderFilePreview = (file) => {
        if (!file) return null;

        const t = file.type;
        if (t.startsWith('image/')) {
            return <img src={URL.createObjectURL(file)} alt="Podgląd" style={{ maxHeight: '100px' }} />;
        }
        if (t.startsWith('video/')) {
            return <video controls src={URL.createObjectURL(file)} style={{ maxHeight: '100px' }} />;
        }
        if (t.startsWith('audio/')) {
            return <audio controls src={URL.createObjectURL(file)} />;
        }
        return <p>{file.name}</p>;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');

        try {
            // 1) Zbuduj listę fizycznych plików do S3
            const physicalFiles = [
                ...questionFiles.map((f, idx) => ({
                    filename: f.name,
                    contentType: f.type,
                    size: f.size,
                    scope: 'question',
                    questionIndex: idx,
                    file: f
                })),
                ...optionFiles.map((f, i) => f ? ({
                    filename: f.name,
                    contentType: f.type,
                    size: f.size,
                    scope: 'option',
                    optionIndex: i,
                    file: f
                }) : null).filter(Boolean)
            ];

            // 2) initiate + upload
            const { questionFilesMeta, optionMediaByIndex } = await uploadAllPhysicalFiles(physicalFiles);

            // 3) Złóż payload JSON
            const payload = {
                exam_id: examId,
                section_id: sectionId,
                type,
                content,
                points: Number(points) || 0,
                negative_points: Number(negativePoints) || 0,
                no_answer_points: Number(noAnswerPoints) || 0,
                tags: tags.split(',').map(t => t.trim()).filter(Boolean),
                questionFiles: questionFilesMeta,
                options: options.map((opt, i) => ({
                    content: opt,
                    is_correct: correctIndexes.includes(i),
                    media: optionMediaByIndex.get(i) || null
                }))
            };

            // 4) Wyślij do backendu (JSON-only)
            const res = await fetch('/api/prowadzacy/pytania', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.blad || 'Błąd dodawania pytania');

            setMessage('✅ Pytanie dodane');
            onQuestionAdded?.();
            // reset formularza
            setContent('');
            setPoints(1);
            setOptions(['', '']);
            setOptionFiles([null, null]);
            setCorrectIndexes([]);
            setTags('');
            setQuestionFiles([]);
        } catch (err) {
            setMessage('❌ ' + (err.message || 'Błąd'));
        }
    };

    return (
        <div style={{ border: '1px solid #ccc', padding: '1rem', marginBottom: '1rem' }}>
            {message && <p>{message}</p>}
            <h4>Dodaj pytanie do sekcji</h4>
            <form onSubmit={handleSubmit}>
                <div>
                    <label>Typ pytania:</label><br />
                    <select value={type} onChange={e => setType(e.target.value)}>
                        <option value="single">Jednokrotny wybór</option>
                        <option value="multi">Wielokrotny wybór</option>
                        <option value="text">Pole tekstowe</option>
                        <option value="dropdown">Lista rozwijana</option>
                        <option value="numeric">Pole numeryczne</option>
                        <option value="essay">Esej</option>
                        <option value="textarea">Obszar tekstowy</option>
                        <option value="file">Załącznik (plik)</option>
                        <option value="oral">Pytanie ustne (nagranie)</option>
                        <option value="photo">Zdjęcie</option>
                        <option value="whiteboard">Whiteboard</option>
                    </select>
                </div>

                <div style={{marginTop:15}}>
                    <label>Treść pytania:</label><br />
                    <textarea value={content} onChange={e => setContent(e.target.value)} required />
                </div>

                <div style={{marginTop:15}}>
                    <label>Pliki do pytania:</label>
                    <input
                        type="file"
                        multiple
                        onChange={handleQuestionFilesChange}
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                    />
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        {questionFiles.map((file, i) => (
                            <div key={i} style={{ position: 'relative' }}>
                                {renderFilePreview(file)}
                                <button
                                    type="button"
                                    onClick={() => removeQuestionFile(i)}
                                    style={{ position: 'absolute', top: 0, right: 0, background: 'red', color: 'white' }}
                                >
                                    ❌
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {['single', 'multi', 'dropdown'].includes(type) && (
                    <div style={{ marginTop: 30 }}>
                        <label>Odpowiedzi:</label><br />
                        {options.map((opt, i) => {
                            const cid = `opt-${i}`;
                            return (
                                <div
                                    key={i}
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        marginBottom: '2rem',
                                        marginTop: '1rem',
                                    }}
                                >
                                    {/* 1) Input na połowie strony */}
                                    <input
                                        type="text"
                                        value={opt}
                                        onChange={e => handleOptionChange(i, e.target.value)}
                                        placeholder={`Opcja ${i + 1}`}
                                        required
                                        style={{
                                            flex: '0 1 50vw',        // połowa szerokości widoku; chcesz połowę kontenera? użyj '50%'
                                            minWidth: 280,
                                            padding: '0.5rem 0.6rem',
                                            border: '1px solid #d1d5db',
                                            borderRadius: 8,
                                        }}
                                    />

                                    {/* 2) „Poprawna” obok inputa */}
                                    <label
                                        htmlFor={cid}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem' }}
                                    >
                                        <input
                                            id={cid}
                                            type={type === 'single' ? 'radio' : 'checkbox'}
                                            name={type === 'single' ? 'correct' : undefined}   // radio muszą mieć wspólny name
                                            checked={correctIndexes.includes(i)}
                                            onChange={() => handleToggleCorrect(i)}
                                            style={{ transform: 'scale(1.25)', transformOrigin: 'left center' }}
                                        />
                                        Poprawna
                                    </label>

                                    {/* 3) Upload PONIŻEJ (pełna szerokość) */}
                                    <button
                                        type="button"
                                        className={'but-rozwin'}
                                        onClick={() => handleRemoveOption(i)}
                                        disabled={options.length <= 1}
                                        title={options.length <= 1 ? 'Musi zostać co najmniej jedna odpowiedź' : 'Usuń odpowiedź'}
                                    >
                                        Usuń
                                    </button>

                                    <div style={{ flexBasis: '100%' }}>
                                        <input
                                            type="file"
                                            onChange={e => handleOptionFileChange(i, e.target.files?.[0] ?? null)}
                                            accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                                        />
                                    </div>

                                    {/* Podgląd pliku też pod spodem */}
                                    {optionFiles[i] && (
                                        <div style={{ flexBasis: '100%' }}>
                                            {renderFilePreview(optionFiles[i])}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <button type="button" className={'but-rozwin'} onClick={handleAddOption}>Dodaj opcję</button>
                    </div>
                )}

                <div style={{ padding: '20px' }}>
                    <label>Punkty za poprawną odpowiedź:</label>
                    <input type="number" value={points} onChange={e => setPoints(e.target.value)} /><br />
                    <label>Punkty za brak odpowiedzi:</label>
                    <input type="number" value={noAnswerPoints} onChange={e => setNoAnswerPoints(e.target.value)} /><br />
                    <label>Punkty za błędną odpowiedź:</label>
                    <input type="number" value={negativePoints} onChange={e => setNegativePoints(e.target.value)} /><br />
                </div>

                <div>
                    <label>Tagi (oddzielone przecinkami):</label><br />
                    <input
                        type="text"
                        value={tags}
                        onChange={e => setTags(e.target.value)}
                        placeholder="np. anatomia, kończyna dolna"
                    />
                </div>

                <button type="submit" className={'but-smal'}   style={{ marginTop: '1rem' }}>Zapisz pytanie</button>
            </form>
        </div>
    );
}

export default TeacherAddQuestion;
